<template>
    <BModal ref="modal" :title="$t('firmwareUpdate.title', { device })" centered no-close-on-backdrop no-close-on-esc no-header-close
        :header-class="`modal-title ${error ? 'text-danger' : ''}`" modal-class="no-border-modal" @hidden="onHidden">
        <!-- Confirmation before launching the update -->
        <div v-if="phase === 'confirm'" class="text-center">
            <p class="mb-3">{{ $t('componentEditHub.confirmFirmwareUpdate', { device }) }}</p>
            <p class="text-danger fw-bold mb-0">{{ $t('firmwareUpdate.warning', { device }) }}</p>
        </div>
        <!-- Update running -->
        <div v-else-if="!error" class="text-center">
            <i class="mdi mdi-update text-primary" style="font-size: 48px;"></i>
            <div class="d-flex justify-content-center py-3">
                <div class="spinner-border text-primary" role="status"></div>
            </div>
            <p class="mb-1">{{ currentMessage }}</p>
            <p class="text-muted mb-3">{{ $t('firmwareUpdate.elapsed', { time: elapsedLabel }) }}</p>
            <p class="text-danger fw-bold mb-0">{{ $t('firmwareUpdate.warning', { device }) }}</p>
        </div>
        <!-- Error displayed by the modal itself -->
        <div v-else class="text-center">
            <i class="mdi mdi-alert-circle text-danger" style="font-size: 48px;"></i>
            <p class="mb-0 mt-2" style="white-space: pre-line;">{{ error }}</p>
            <p v-if="showDetails" class="font-13 text-muted mb-0 mt-2" style="white-space: pre-line; word-break: break-word;">{{ errorDetails }}</p>
        </div>
        <template #footer>
            <div class="d-flex justify-content-between align-items-center w-100">
                <button type="button" class="btn btn-secondary" :disabled="running" @click="close(null)">{{ phase === 'confirm' ? $t('common.cancel') : $t('common.close') }}</button>
                <button v-if="phase === 'confirm'" type="button" class="btn btn-primary ms-2" @click="run">{{ $t('componentEditHub.update') }}</button>
                <div v-else-if="error">
                    <button v-if="errorDetails && !showDetails" type="button" class="btn btn-outline-secondary" @click="showDetails = true">{{ $t('firmwareUpdate.details') }}</button>
                    <button type="button" class="btn btn-primary ms-2" @click="run">{{ $t('firmwareUpdate.retry') }}</button>
                </div>
            </div>
        </template>
    </BModal>
</template>

<script>
import { BModal } from 'bootstrap-vue-next';
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';

// The update result is reported by the device once it is back: poll it slowly
const RESULT_POLL_INTERVAL = 15000;
// Overall cap for the whole update, reboots included (the device announces up to 10 minutes)
const RESULT_TIMEOUT = 15 * 60 * 1000;

// Translates a stable `err_update_*` code reported by the firmware updater
function firmwareUpdateErrorMessage(t, code) {
    const known = {
        err_update_no_server: 'errorNoServer',
        err_update_server_unreachable: 'errorServerUnreachable',
        err_update_server_status: 'errorServerStatus',
        err_update_invalid_server_answer: 'errorInvalidServerAnswer',
        err_update_invalid_version: 'errorInvalidVersion',
        err_update_invalid_order: 'errorInvalidOrder',
        err_update_download: 'errorDownload',
        err_update_io: 'errorIo',
        err_update_apply: 'errorApply',
        err_update_apply_output: 'errorApplyOutput',
        err_update_post_check: 'errorPostCheck',
        err_update_internal: 'errorInternal',
    };
    const key = known[code];
    return key ? t(`firmwareUpdate.${key}`) : t('firmwareUpdate.errorUnknown', { code });
}

// Drives a firmware update of a hub / link, as the Haxo Setup app does without its BLE fallback:
//  1. asks the installer to confirm;
//  2. asks the server to order the update (the device must be connected through MQTT);
//  3. once ordered, waits for the result the device reports to the server after its reboot
//     (polled slowly until it is available).
// Errors are displayed by the modal itself, with a retry.
// show(facilityId, hub) resolves with { upToDate, rebooted, version }, or null when cancelled
// or closed after an error.
export default {
    name: 'FirmwareUpdateModal',
    components: {
        BModal
    },
    setup() {
        const { t } = useI18n();
        const userStore = useUserStore();
        const modal = ref(null);
        const device = ref('');
        const phase = ref('confirm');       // 'confirm' | 'ordering' | 'waitingResult'
        const error = ref(null);
        const errorDetails = ref('');
        const showDetails = ref(false);
        const elapsed = ref(0);
        let target = null;
        let resolver = null;
        let run_ = 0;
        let start = 0;
        let ticker = null;

        const running = computed(() => phase.value !== 'confirm' && !error.value);

        const currentMessage = computed(() => phase.value === 'ordering'
            ? t('firmwareUpdate.ordering')
            : t('firmwareUpdate.rebooting', { device: device.value }));

        const elapsedLabel = computed(() => {
            const seconds = Math.floor(elapsed.value / 1000);
            return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
        });

        const stopTicker = () => {
            if (ticker) clearInterval(ticker);
            ticker = null;
        };

        const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

        const fail = (message, details) => {
            error.value = message;
            errorDetails.value = details || '';
            showDetails.value = false;
            stopTicker();
        };

        const close = (result) => {
            const resolve = resolver;
            resolver = null;
            run_++;
            stopTicker();
            modal.value.hide();
            if (resolve) resolve(result);
        };

        const onHidden = () => {
            if (resolver) close(null);
        };

        const handleResult = (data) => {
            if (!data || typeof data !== 'object') {
                fail(t('firmwareUpdate.unexpectedError'), String(data));
                return;
            }
            const status = String(data.status ?? '').toLowerCase();
            const version = data.version != null ? String(data.version) : null;
            if (status === 'success') {
                close({ upToDate: false, rebooted: true, version });
                return;
            }
            const code = String(data.code ?? '');
            fail(t('firmwareUpdate.failed', { reason: firmwareUpdateErrorMessage(t, code) }), data.detail != null ? String(data.detail) : '');
        };

        // Polls the update result reported by the device to the server
        const waitForResult = async (current) => {
            phase.value = 'waitingResult';
            for (;;) {
                // The cap covers the whole update: measured from the start of the run
                if (Date.now() - start > RESULT_TIMEOUT) {
                    fail(t('firmwareUpdate.failed', { reason: t('firmwareUpdate.errorNoResult') }));
                    return;
                }
                await sleep(RESULT_POLL_INTERVAL);
                if (current !== run_) return;

                let result;
                try {
                    result = await userStore.readFirmwareUpdateResult(target.hub);
                } catch (message) {
                    if (current === run_) fail(message);
                    return;
                }
                if (current !== run_) return;
                if (result) {
                    handleResult(result);
                    return;
                }
                // No result yet: the device is still updating / rebooting
            }
        };

        const run = async () => {
            const current = ++run_;
            error.value = null;
            errorDetails.value = '';
            showDetails.value = false;
            phase.value = 'ordering';
            start = Date.now();
            elapsed.value = 0;
            stopTicker();
            ticker = setInterval(() => { elapsed.value = Date.now() - start; }, 1000);

            let order;
            try {
                order = await userStore.orderFirmwareUpdate(target.facilityId, target.hub);
            } catch (message) {
                if (current === run_) fail(message);
                return;
            }
            if (current !== run_) return;

            if (order === 'uptodate') {
                close({ upToDate: true, rebooted: false, version: null });
                return;
            }
            // Update ordered: the result is read from the server once the device is back
            await waitForResult(current);
        };

        const show = (facilityId, hub) => {
            target = { facilityId, hub };
            device.value = hub.productName;
            phase.value = 'confirm';
            error.value = null;
            errorDetails.value = '';
            showDetails.value = false;
            elapsed.value = 0;
            modal.value.show();
            return new Promise(resolve => { resolver = resolve; });
        };

        onBeforeUnmount(stopTicker);

        return {
            modal,
            device,
            phase,
            error,
            errorDetails,
            showDetails,
            running,
            currentMessage,
            elapsedLabel,
            run,
            show,
            close,
            onHidden,
        };
    }
}
</script>
